import React, { useState, useEffect, useMemo } from 'react'

import {
    Divider,
    IconButton,
    Modal,
    Paper,
    Slide,
    Tooltip,
    useTheme,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@material-ui/core'
import {
    Close,
    Add,
    Edit,
    Visibility,
    Delete,
    Assignment,
    AddBox,
} from '@material-ui/icons'
import { ReactComponent as ActionItemIcon } from '../../assets/icons/action-item.svg'

import {
    AreaConfig,
    InventoryConfig,
    InventoryInspection,
    Issue,
    IssueCreationType,
    Lease,
    ListVendor,
    ModelListMap,
    ModelMap,
    Schedule,
    Service,
    Unit,
    User,
    getIssueCount,
} from '../../models'
import { toMMDDYYYY } from '../../helpers'
import { Attachment, Container } from '../../components'
import { IssueImage } from '../../components/IssueImage/IssueImage'
import { IssueWorkorderModal } from './IssueWorkorderModal'
import { useWorkorderApi } from '../../contexts'
import { toast } from 'react-toastify'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    handleClose: () => void
    areaConfigMap: ModelMap<AreaConfig>
    invConfigMap: ModelMap<InventoryConfig>
    unitMap: ModelMap<Unit>
    lease?: Lease
    serviceList: Service[]
    vendorList: ListVendor[]
    userList: User[]
    scheduleList: Schedule[]
    onLeaseUpdate?: (updatedLease: Lease) => void
    addIssueToInspection?: (
        inventoryInspectionId: number,
        description: string,
    ) => Promise<any>
    addImageToIssue?: (issueId: number, file: File) => Promise<any>
    removeImageFromIssue?: (issueId: number, imageId: number) => Promise<any>
    removeIssue?: (issueId: number) => Promise<any>
    readOnly?: boolean
    hideIssueCount?: boolean
}

export const MoveInModal = (props: Props) => {
    const {
        open,
        handleClose,
        lease: parentLease,
        areaConfigMap,
        unitMap,
        invConfigMap,
        onLeaseUpdate,
        addIssueToInspection,
        addImageToIssue,
        removeImageFromIssue,
        removeIssue,
        readOnly = false,
        hideIssueCount = false,
        serviceList,
        vendorList,
        userList,
        scheduleList,
    } = props

    const { createWorkorders } = useWorkorderApi()
    const [workorderLoading, setWorkorderLoading] = useState(false)

    const theme = useTheme()
    const [isEditMode, setIsEditMode] = useState(false)
    const [customIssue, setCustomIssue] = useState('')
    const [selectedInventoryConfig, setSelectedInventoryConfig] = useState<
        number | null
    >(null)
    const [localLease, setLocalLease] = useState<Lease | undefined>(parentLease)
    const [deleteIssueDialog, setDeleteIssueDialog] = useState<{
        open: boolean
        issue: Issue | null
    }>({ open: false, issue: null })
    const [deleteImageDialog, setDeleteImageDialog] = useState<{
        open: boolean
        issueId: number | null
        imageId: number | null
    }>({ open: false, issueId: null, imageId: null })
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedIssues, setSelectedIssues] = useState<Issue[]>([])
    const [openWorkorderModal, setOpenWorkorderModal] = useState(false)

    // Update local lease when parent lease changes
    useEffect(() => {
        setLocalLease(parentLease)
    }, [parentLease])

    // Reset selection when modal closes
    useEffect(() => {
        if (!open) {
            setSelectionMode(false)
            setSelectedIssues([])
            setOpenWorkorderModal(false)
        }
    }, [open])

    const flatLeaseList = [
        localLease as Lease,
        ...((localLease?.children ?? []) as Lease[]),
    ]

    let startDate = ''
    let endDate = ''
    if (localLease) {
        startDate = toMMDDYYYY(new Date(localLease.start_date))
        endDate = toMMDDYYYY(new Date(localLease.end_date))
    }

    const issueCount = localLease ? getIssueCount(localLease) : 0

    // Function to update lease state locally and notify parent
    const updateLeaseState = (updatedLease: Lease) => {
        setLocalLease(updatedLease)
        onLeaseUpdate?.(updatedLease)
    }

    // Function to add a new issue to an inventory inspection
    const addIssue = async (
        inventoryInspection: InventoryInspection,
        customIssue: string,
    ) => {
        if (!customIssue.trim() || !localLease || !addIssueToInspection) return

        try {
            const response = await addIssueToInspection(
                inventoryInspection.id,
                customIssue,
            )

            // Create updated lease with new issue
            const updatedLease = { ...localLease }

            // First, try to find the inspection in the parent lease
            let targetInspection = updatedLease.tenant_move_in_inspection?.inventory_inspection?.find(
                (inv) => inv.id === inventoryInspection.id,
            )

            // If not found in parent, check children leases
            if (!targetInspection && updatedLease.children) {
                for (const childLease of updatedLease.children) {
                    targetInspection = childLease.tenant_move_in_inspection?.inventory_inspection?.find(
                        (inv) => inv.id === inventoryInspection.id,
                    )
                    if (targetInspection) {
                        // Found it in a child lease, update that child lease
                        if (!targetInspection.issue) {
                            targetInspection.issue = []
                        }
                        targetInspection.issue.push(response)
                        break
                    }
                }
            } else if (targetInspection) {
                // Found it in parent lease, update parent lease
                if (!targetInspection.issue) {
                    targetInspection.issue = []
                }
                targetInspection.issue.push(response)
            }

            updateLeaseState(updatedLease)
            setCustomIssue('')
            setSelectedInventoryConfig(null)
        } catch (error) {
            console.error('Error adding issue:', error)
        }
    }

    // Function to show delete issue dialog
    const showDeleteIssueDialog = (issue: Issue) => {
        setDeleteIssueDialog({ open: true, issue })
    }

    // Function to confirm delete issue
    const confirmDeleteIssue = async () => {
        const { issue } = deleteIssueDialog
        if (!issue || !localLease || !removeIssue) {
            setDeleteIssueDialog({ open: false, issue: null })
            return
        }

        try {
            await removeIssue(issue.id)

            // Create updated lease without the removed issue
            const updatedLease = { ...localLease }

            // Helper function to remove issues from a lease
            const removeIssuesFromLease = (lease: Lease) => {
                lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                    (inv) => {
                        if (inv.issue) {
                            inv.issue = inv.issue.filter(
                                (iss) => iss.id !== issue.id,
                            )
                        }
                    },
                )
            }

            // Remove issues from parent lease
            removeIssuesFromLease(updatedLease)

            // Remove issues from children leases
            if (updatedLease.children) {
                updatedLease.children.forEach((childLease) => {
                    removeIssuesFromLease(childLease)
                })
            }

            updateLeaseState(updatedLease)
        } catch (error) {
            console.error('Error removing issue:', error)
        } finally {
            setDeleteIssueDialog({ open: false, issue: null })
        }
    }

    // Function to handle image updates
    const handleImageUpdate = (issueId: number, responseData: any) => {
        // Update the lease data with the new message data
        if (localLease) {
            const updatedLease = { ...localLease }

            // Helper function to update issues in a lease
            const updateIssuesInLease = (lease: Lease) => {
                lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                    (inv) => {
                        if (inv.issue) {
                            inv.issue = inv.issue.map((innerIssue) => {
                                if (issueId !== innerIssue.id) {
                                    return innerIssue
                                }
                                return {
                                    ...innerIssue,
                                    message: responseData,
                                }
                            })
                        }
                    },
                )
            }

            // Update issues in parent lease
            updateIssuesInLease(updatedLease)

            // Update issues in children leases
            if (updatedLease.children) {
                updatedLease.children.forEach((childLease) => {
                    updateIssuesInLease(childLease)
                })
            }

            updateLeaseState(updatedLease)
        }
    }

    // Function to show delete image dialog
    const showDeleteImageDialog = (issueId: number, imageId: number) => {
        setDeleteImageDialog({ open: true, issueId, imageId })
    }

    // Function to confirm delete image
    const confirmDeleteImage = async () => {
        const { issueId, imageId } = deleteImageDialog
        if (!issueId || !imageId || !removeImageFromIssue) {
            setDeleteImageDialog({ open: false, issueId: null, imageId: null })
            return
        }

        try {
            await removeImageFromIssue(issueId, imageId)

            // Update the lease data by removing the image from the issue
            if (localLease) {
                const updatedLease = { ...localLease }

                // Helper function to remove images from issues in a lease
                const removeImagesFromLease = (lease: Lease) => {
                    lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                        (inv) => {
                            if (inv.issue) {
                                inv.issue = inv.issue.map((innerIssue) => {
                                    if (issueId !== innerIssue.id) {
                                        return innerIssue
                                    }
                                    return {
                                        ...innerIssue,
                                        message: {
                                            ...innerIssue.message,
                                            attachments:
                                                innerIssue.message.attachments?.filter(
                                                    (attachment) =>
                                                        attachment.id !==
                                                        imageId,
                                                ) || [],
                                        },
                                    }
                                })
                            }
                        },
                    )
                }

                // Remove images from parent lease
                removeImagesFromLease(updatedLease)

                // Remove images from children leases
                if (updatedLease.children) {
                    updatedLease.children.forEach((childLease) => {
                        removeImagesFromLease(childLease)
                    })
                }

                updateLeaseState(updatedLease)
            }
        } catch (error) {
            console.error('Error removing image:', error)
        } finally {
            setDeleteImageDialog({ open: false, issueId: null, imageId: null })
        }
    }

    // Function to handle issue selection
    const handleIssueSelection = (issue: Issue) => {
        // Don't allow selection of issues that already have workorders
        if (issue.workorder_id) {
            return
        }

        if (
            selectedIssues.find(
                (selectedIssue) => selectedIssue.id === issue.id,
            )
        ) {
            // Remove from selection
            setSelectedIssues(
                selectedIssues.filter(
                    (selectedIssue) => selectedIssue.id !== issue.id,
                ),
            )
        } else {
            // Add to selection
            setSelectedIssues([...selectedIssues, issue])
        }
    }

    // Function to check if issue is selected
    const isIssueSelected = (issue: Issue) => {
        return (
            selectedIssues.find(
                (selectedIssue) => selectedIssue.id === issue.id,
            ) !== undefined
        )
    }

    const leaseAreas = useMemo(() => {
        return (
            flatLeaseList.filter((lease) => lease).map((lease) => lease.area) ??
            []
        )
    }, [flatLeaseList])

    // Function to update issue state when workorders are created
    const updateIssueWithWorkorder = (issueId: number, workorderId: number) => {
        if (localLease) {
            const updatedLease = { ...localLease }

            // Helper function to update issues in a lease
            const updateIssuesInLease = (lease: Lease) => {
                lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                    (inv) => {
                        if (inv.issue) {
                            inv.issue = inv.issue.map((innerIssue) => {
                                if (issueId !== innerIssue.id) {
                                    return innerIssue
                                }
                                return {
                                    ...innerIssue,
                                    workorder_id: workorderId,
                                }
                            })
                        }
                    },
                )
            }

            // Update issues in parent lease
            updateIssuesInLease(updatedLease)

            // Update issues in children leases
            if (updatedLease.children) {
                updatedLease.children.forEach((childLease) => {
                    updateIssuesInLease(childLease)
                })
            }

            updateLeaseState(updatedLease)
        }
    }

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper
                        style={{
                            display: 'flex',
                            overflowY: 'hidden',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            outline: 'none',
                            borderRadius: '20px',
                            width: 1200,
                            maxWidth: 'calc(100vw - 200px)',
                            height: 700,
                            padding: theme.spacing(1, 4, 1, 4),
                        }}
                    >
                        <Container
                            style={{
                                padding: theme.spacing(3, 0, 4, 2),
                                flex: 1,
                                overflowY: 'scroll',
                            }}
                            direction="column"
                        >
                            {/* Header */}
                            <Container
                                style={{
                                    marginBottom: theme.spacing(1),
                                    flexDirection: 'column',
                                }}
                            >
                                <Container>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h5,
                                            ),
                                            fontWeight: 'bold',
                                            alignSelf: 'center',
                                        }}
                                    >
                                        Move In Result
                                        {!hideIssueCount &&
                                            `: (${issueCount}) Issues Found`}
                                    </span>
                                    <div style={{ flex: 1 }} />

                                    {/* Edit Mode Toggle - only show if not readOnly */}
                                    {!readOnly && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isEditMode}
                                                    onChange={(e) =>
                                                        setIsEditMode(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Container
                                                    style={{
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {isEditMode ? (
                                                        <Edit />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                    <span
                                                        style={{
                                                            marginLeft: theme.spacing(
                                                                0.5,
                                                            ),
                                                        }}
                                                    >
                                                        {isEditMode
                                                            ? 'Edit Mode'
                                                            : 'View Mode'}
                                                    </span>
                                                </Container>
                                            }
                                            style={{
                                                marginRight: theme.spacing(2),
                                            }}
                                        />
                                    )}

                                    {/* Selection Mode Toggle and Create Workorders Button */}
                                    {!readOnly && (
                                        <>
                                            {selectionMode && (
                                                <Container
                                                    style={{
                                                        alignItems: 'center',
                                                        marginRight: theme.spacing(
                                                            2,
                                                        ),
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            ...typographyStyle(
                                                                theme.typography
                                                                    .h6,
                                                            ),
                                                            fontWeight:
                                                                theme.typography
                                                                    .fontWeightBold,
                                                            marginRight: theme.spacing(
                                                                2,
                                                            ),
                                                        }}
                                                    >
                                                        {selectedIssues.length}{' '}
                                                        Selected
                                                    </span>
                                                    <Button
                                                        disabled={
                                                            selectedIssues.length ===
                                                            0
                                                        }
                                                        variant="contained"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .primary
                                                                    .dark,
                                                            color: 'white',
                                                            textTransform:
                                                                'none',
                                                        }}
                                                        onClick={() =>
                                                            setOpenWorkorderModal(
                                                                true,
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        + Create Workorders
                                                    </Button>
                                                </Container>
                                            )}
                                            <Tooltip title="Select Issues">
                                                <IconButton
                                                    onClick={() =>
                                                        setSelectionMode(
                                                            !selectionMode,
                                                        )
                                                    }
                                                    color={
                                                        selectionMode
                                                            ? 'primary'
                                                            : 'default'
                                                    }
                                                    style={{
                                                        marginRight: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}

                                    <Tooltip title="Close">
                                        <IconButton onClick={handleClose}>
                                            <Close fontSize="large" />
                                        </IconButton>
                                    </Tooltip>
                                </Container>
                                <Container>
                                    <Container
                                        style={{ flexDirection: 'column' }}
                                    >
                                        <span
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            Tenant: {localLease?.user.name}
                                        </span>
                                        <span>
                                            Email: {localLease?.user.email}
                                        </span>
                                    </Container>
                                    <div style={{ flex: 1 }} />
                                    <Container
                                        style={{ flexDirection: 'column' }}
                                    >
                                        <span
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            Location:{' '}
                                            {
                                                unitMap[
                                                    localLease?.area.unit ?? -1
                                                ]?.name
                                            }
                                        </span>
                                        <span>
                                            Date: {startDate} - {endDate}
                                        </span>
                                    </Container>
                                </Container>
                            </Container>

                            <Divider />

                            {/* Body */}
                            <Container
                                style={{
                                    flex: 1,
                                    overflowY: 'scroll',
                                }}
                                direction="column"
                            >
                                {/* Move In Results */}
                                {flatLeaseList.map((lease, idx) => {
                                    const areaNameAndLabel =
                                        areaConfigMap[
                                            lease?.area.area_config ?? -1
                                        ]?.name +
                                        ' (' +
                                        lease?.area.area_label +
                                        ')'

                                    const invConfigList: InventoryConfig[] = []
                                    // key in dict is inv_conf id
                                    const moveInIssues: ModelListMap<Issue> = {}
                                    lease?.tenant_move_in_inspection?.inventory_inspection?.forEach(
                                        (inv_ins) => {
                                            const config =
                                                invConfigMap[
                                                    inv_ins.inventory_config
                                                ]
                                            if (config) {
                                                invConfigList.push(config)
                                            }
                                            moveInIssues[
                                                inv_ins.inventory_config
                                            ] = []
                                            inv_ins.issue?.forEach((iss) => {
                                                moveInIssues[
                                                    inv_ins.inventory_config
                                                ]?.push(iss)
                                            })
                                        },
                                    )

                                    return (
                                        <Container
                                            style={{
                                                borderRadius:
                                                    theme.shape.borderRadius,
                                                border: `3px solid ${theme.palette.grey[400]}`,
                                                flexDirection: 'column',
                                                flex: 1,
                                                marginBottom: theme.spacing(2),
                                            }}
                                            key={`INNER_LEASE_${idx}_${lease?.id}_MOVE_IN_MODAL`}
                                        >
                                            <Container
                                                style={{
                                                    borderRadius:
                                                        theme.shape
                                                            .borderRadius,
                                                    borderBottom: `3px solid ${theme.palette.grey[400]}`,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        padding: theme.spacing(
                                                            2,
                                                        ),
                                                        ...typographyStyle(
                                                            theme.typography.h6,
                                                        ),
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {areaNameAndLabel}
                                                </span>

                                                {lease?.tenant_move_in_inspection &&
                                                    lease
                                                        ?.tenant_move_in_inspection
                                                        ?.auto_completed ===
                                                        1 && (
                                                        <span
                                                            style={{
                                                                ...typographyStyle(
                                                                    theme
                                                                        .typography
                                                                        .body1,
                                                                ),
                                                                alignSelf:
                                                                    'center',
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            Auto Completed
                                                        </span>
                                                    )}
                                            </Container>

                                            {invConfigList.map((invConfig) => {
                                                return (
                                                    <MoveInResultInvItem
                                                        key={`MOVE_IN_INV_${invConfig.id}`}
                                                        invConfig={invConfig}
                                                        moveInIssues={
                                                            moveInIssues
                                                        }
                                                        lease={lease}
                                                        isEditMode={isEditMode}
                                                        readOnly={readOnly}
                                                        selectionMode={
                                                            selectionMode
                                                        }
                                                        onAddIssue={addIssue}
                                                        onRemoveIssue={
                                                            showDeleteIssueDialog
                                                        }
                                                        onImageUpdate={
                                                            handleImageUpdate
                                                        }
                                                        onRemoveImage={
                                                            showDeleteImageDialog
                                                        }
                                                        onIssueSelection={
                                                            handleIssueSelection
                                                        }
                                                        isIssueSelected={
                                                            isIssueSelected
                                                        }
                                                        selectedInventoryConfig={
                                                            selectedInventoryConfig
                                                        }
                                                        setSelectedInventoryConfig={
                                                            setSelectedInventoryConfig
                                                        }
                                                        customIssue={
                                                            customIssue
                                                        }
                                                        setCustomIssue={
                                                            setCustomIssue
                                                        }
                                                        addImageToIssue={
                                                            addImageToIssue
                                                        }
                                                    />
                                                )
                                            })}
                                        </Container>
                                    )
                                })}
                            </Container>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>

            {/* Delete Issue Confirmation Dialog */}
            <Dialog
                open={deleteIssueDialog.open}
                onClose={() =>
                    setDeleteIssueDialog({ open: false, issue: null })
                }
                aria-labelledby="delete-issue-dialog-title"
            >
                <DialogTitle id="delete-issue-dialog-title">
                    Delete Issue
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete this issue?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteIssueDialog({ open: false, issue: null })
                        }
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteIssue}
                        color="secondary"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Image Confirmation Dialog */}
            <Dialog
                open={deleteImageDialog.open}
                onClose={() =>
                    setDeleteImageDialog({
                        open: false,
                        issueId: null,
                        imageId: null,
                    })
                }
                aria-labelledby="delete-image-dialog-title"
            >
                <DialogTitle id="delete-image-dialog-title">
                    Delete Image
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete this image?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteImageDialog({
                                open: false,
                                issueId: null,
                                imageId: null,
                            })
                        }
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteImage}
                        color="secondary"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Issue Workorder Modal */}
            <IssueWorkorderModal
                open={openWorkorderModal}
                serviceList={serviceList}
                vendorList={vendorList}
                userList={userList}
                issues={selectedIssues}
                scheduleList={scheduleList}
                areas={leaseAreas}
                loading={workorderLoading}
                handleClose={() => setOpenWorkorderModal(false)}
                onClickCreate={(request) => {
                    setWorkorderLoading(true)
                    createWorkorders(request)
                        .then((response) => {
                            // Show success toast
                            toast.success('Workorders created successfully!')

                            // Update issue states with workorder IDs
                            if (
                                response &&
                                response.workorders &&
                                Array.isArray(response.workorders)
                            ) {
                                response.workorders.forEach(
                                    (workorder: any) => {
                                        if (workorder.issue_id) {
                                            updateIssueWithWorkorder(
                                                workorder.issue_id,
                                                workorder.id,
                                            )
                                        }
                                    },
                                )
                            }
                            setOpenWorkorderModal(false)
                            setSelectedIssues([])
                            setSelectionMode(false)
                        })
                        .catch((error) => {
                            toast.error(
                                error.response?.data?.message ||
                                    'Error creating workorders',
                            )
                        })
                        .finally(() => {
                            setWorkorderLoading(false)
                        })
                }}
            />
        </>
    )
}

interface MoveOutResultInvItemProps {
    invConfig: InventoryConfig
    moveOutInvIns: ModelMap<InventoryInspection>
}

const MoveOutResultInvItem = (props: MoveOutResultInvItemProps) => {
    const { invConfig, moveOutInvIns } = props

    const theme = useTheme()

    return (
        <Container
            style={{ height: 200, flexDirection: 'column' }}
            key={`MOVE_OUT_INV_${invConfig.id}`}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.h6),
                    padding: theme.spacing(1, 1, 0, 2),
                    fontWeight: 'bold',
                }}
            >
                {invConfig.name}
            </span>
            {moveOutInvIns[invConfig.id] && (
                <Container
                    style={{
                        border: `2px solid ${
                            moveOutInvIns[invConfig.id]?.status?.color
                        }`,
                        backgroundColor: 'white',
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: `2px 2px 6px #ffffff`,
                        padding: theme.spacing(1),
                        margin: theme.spacing(1),
                        flexDirection: 'row',
                        height: 70,
                        minWidth: 200,
                        maxWidth: 200,
                        cursor: 'pointer',
                        alignItems: 'center',
                    }}
                >
                    <span style={typographyStyle(theme.typography.body1)}>
                        {moveOutInvIns[invConfig.id]?.status?.name}
                    </span>
                </Container>
            )}
        </Container>
    )
}

interface MoveInResultInvItemProps {
    invConfig: InventoryConfig
    moveInIssues: ModelListMap<Issue>
    lease: Lease
    isEditMode: boolean
    readOnly: boolean
    selectionMode: boolean
    onAddIssue: (
        inventoryInspection: InventoryInspection,
        customIssue: string,
    ) => Promise<void>
    onRemoveIssue: (issue: Issue) => void
    onImageUpdate: (issueId: number, responseData: any) => void
    onRemoveImage: (issueId: number, imageId: number) => void
    onIssueSelection: (issue: Issue) => void
    isIssueSelected: (issue: Issue) => boolean
    selectedInventoryConfig: number | null
    setSelectedInventoryConfig: (id: number | null) => void
    customIssue: string
    setCustomIssue: (issue: string) => void
    addImageToIssue?: (issueId: number, file: File) => Promise<any>
}

const MoveInResultInvItem = (props: MoveInResultInvItemProps) => {
    const {
        invConfig,
        moveInIssues,
        lease,
        isEditMode,
        readOnly,
        selectionMode,
        onAddIssue,
        onRemoveIssue,
        onImageUpdate,
        onRemoveImage,
        onIssueSelection,
        isIssueSelected,
        selectedInventoryConfig,
        setSelectedInventoryConfig,
        customIssue,
        setCustomIssue,
        addImageToIssue,
    } = props

    const theme = useTheme()

    // Find the inventory inspection for this config
    const inventoryInspection = lease?.tenant_move_in_inspection?.inventory_inspection?.find(
        (inv) => inv.inventory_config === invConfig.id,
    )

    const isSelected = selectedInventoryConfig === invConfig.id
    const issues = moveInIssues[invConfig.id] || []

    return (
        <Container
            style={{
                marginBottom: theme.spacing(1),
                flexDirection: 'column',
            }}
            key={`MOVE_IN_INV_${invConfig.id}`}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.h6),
                    padding: theme.spacing(1, 1, 0, 2),
                    fontWeight: 'bold',
                }}
            >
                {invConfig.name}
            </span>

            {/* Add Issue Form */}
            {isEditMode && !readOnly && (
                <Container
                    style={{
                        margin: theme.spacing(1, 2),
                        padding: theme.spacing(1),
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    {isSelected ? (
                        <Container style={{ flexDirection: 'column' }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    marginBottom: theme.spacing(1),
                                    color: theme.palette.grey[600],
                                }}
                            >
                                Adding issue to {invConfig.name}
                            </span>
                            <Container style={{ alignItems: 'center' }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={customIssue}
                                    onChange={(e) =>
                                        setCustomIssue(e.target.value)
                                    }
                                    placeholder="Describe the issue..."
                                    style={{
                                        flex: 1,
                                        marginRight: theme.spacing(1),
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        if (
                                            inventoryInspection &&
                                            customIssue.trim()
                                        ) {
                                            onAddIssue(
                                                inventoryInspection,
                                                customIssue,
                                            )
                                        }
                                    }}
                                    disabled={!customIssue.trim()}
                                >
                                    Add Issue
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setSelectedInventoryConfig(null)
                                        setCustomIssue('')
                                    }}
                                    style={{ marginLeft: theme.spacing(1) }}
                                >
                                    Cancel
                                </Button>
                            </Container>
                        </Container>
                    ) : (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() =>
                                setSelectedInventoryConfig(invConfig.id)
                            }
                            style={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                            }}
                        >
                            Add Issue
                        </Button>
                    )}
                </Container>
            )}

            {/* Existing Issues */}
            {issues.map((issue) => {
                const displayName = issue.issue_config
                    ? issue.issue_config.name
                    : issue.description
                return (
                    <Container
                        key={`ISSUE_${issue.id}`}
                        style={{
                            position: 'relative',
                            margin: theme.spacing(1, 1, 1, 5),
                        }}
                    >
                        {/* Workorder icon - positioned absolutely in the left margin space */}
                        {issue.workorder_id && (
                            <Tooltip title="Workorder Created">
                                <ActionItemIcon
                                    height={20}
                                    width={20}
                                    style={{
                                        position: 'absolute',
                                        left: -25,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1,
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Issue box - unchanged layout */}
                        <Container
                            style={{
                                flexDirection: 'row',
                                border: `1px solid ${theme.palette.grey[900]}`,
                                borderRadius: theme.shape.borderRadius,
                                height: 150,
                                overflowY: 'scroll',
                                alignItems: 'center',
                                backgroundColor:
                                    selectionMode && isIssueSelected(issue)
                                        ? theme.palette.grey[300]
                                        : undefined,
                                cursor:
                                    selectionMode && !issue.workorder_id
                                        ? 'pointer'
                                        : 'default',
                                width: '100%',
                            }}
                            onClick={() => {
                                if (selectionMode) {
                                    onIssueSelection(issue)
                                }
                            }}
                        >
                            {/* Left side: Issue description */}
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    flex: 1,
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.body1,
                                            ),
                                            overflowY: 'scroll',
                                            flex: 1,
                                        }}
                                    >
                                        {displayName}
                                    </span>
                                </Container>
                            </Container>

                            {/* Right side: Images and image management */}
                            <Container
                                style={{
                                    padding: theme.spacing(1),
                                    paddingBottom: 0,
                                    paddingRight: 0,
                                    alignItems: 'center',
                                    overflowX: 'auto',
                                    minWidth: 320,
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {/* Show existing attachments */}
                                {issue.message.attachments &&
                                issue.message.attachments.length > 0
                                    ? issue.message.attachments.map(
                                          (attachment) => {
                                              return (
                                                  <div
                                                      key={`ATTACHMENT_${attachment.id}`}
                                                      style={{
                                                          position: 'relative',
                                                          margin: theme.spacing(
                                                              0,
                                                              0.5,
                                                          ),
                                                      }}
                                                  >
                                                      <Attachment
                                                          attachment={
                                                              attachment.file
                                                          }
                                                          uploaded={
                                                              attachment.uploaded
                                                          }
                                                          theme={theme}
                                                          size={90}
                                                      />
                                                      {/* Remove image button - only show in edit mode and not readOnly */}
                                                      {isEditMode &&
                                                          !readOnly &&
                                                          issue.creation_type ===
                                                              IssueCreationType.APARTMENT && (
                                                              <Tooltip title="Delete Image">
                                                                  <IconButton
                                                                      size="small"
                                                                      onClick={() =>
                                                                          onRemoveImage(
                                                                              issue.id,
                                                                              attachment.id,
                                                                          )
                                                                      }
                                                                      style={{
                                                                          position:
                                                                              'absolute',
                                                                          top: -8,
                                                                          right: -8,
                                                                          backgroundColor:
                                                                              theme
                                                                                  .palette
                                                                                  .error
                                                                                  .main,
                                                                          color:
                                                                              'white',
                                                                          width: 20,
                                                                          height: 20,
                                                                      }}
                                                                  >
                                                                      <Delete
                                                                          style={{
                                                                              fontSize: 12,
                                                                          }}
                                                                      />
                                                                  </IconButton>
                                                              </Tooltip>
                                                          )}
                                                  </div>
                                              )
                                          },
                                      )
                                    : null}

                                {/* Add IssueImage components for additional image slots in edit mode */}
                                {isEditMode &&
                                    !readOnly &&
                                    inventoryInspection && (
                                        <>
                                            <IssueImage
                                                index={0}
                                                issue={issue}
                                                inventoryInspection={
                                                    inventoryInspection
                                                }
                                                onImageUpdate={onImageUpdate}
                                                addImageToIssue={
                                                    addImageToIssue
                                                }
                                            />
                                            <IssueImage
                                                index={1}
                                                issue={issue}
                                                inventoryInspection={
                                                    inventoryInspection
                                                }
                                                onImageUpdate={onImageUpdate}
                                                addImageToIssue={
                                                    addImageToIssue
                                                }
                                            />
                                            <IssueImage
                                                index={2}
                                                issue={issue}
                                                inventoryInspection={
                                                    inventoryInspection
                                                }
                                                onImageUpdate={onImageUpdate}
                                                addImageToIssue={
                                                    addImageToIssue
                                                }
                                            />
                                        </>
                                    )}
                            </Container>

                            {/* Issue delete button - far right of row */}
                            {isEditMode &&
                                !readOnly &&
                                issue.creation_type ===
                                    IssueCreationType.APARTMENT && (
                                    <Container
                                        style={{
                                            padding: theme.spacing(1),
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Tooltip title="Delete Issue">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onRemoveIssue(issue)
                                                }
                                                style={{
                                                    color:
                                                        theme.palette.error
                                                            .main,
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                )}
                        </Container>
                    </Container>
                )
            })}

            {/* No Issues Message */}
            {issues.length === 0 && (
                <Container
                    style={{
                        margin: theme.spacing(1, 5),
                        padding: theme.spacing(2),
                        border: `1px dashed ${theme.palette.grey[400]}`,
                        borderRadius: theme.shape.borderRadius,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.grey[500],
                            fontStyle: 'italic',
                        }}
                    >
                        No issues found for {invConfig.name}
                    </span>
                </Container>
            )}
        </Container>
    )
}
